import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/formatters'
import { Clock, CalendarClock, Phone, User, Bike, ShoppingBag, UtensilsCrossed } from 'lucide-react'
import { OrderStatusBadge } from './OrderStatusBadge'
import {
  formatTime,
  formatRelativeTime,
  getOrderType,
  getConfirmationBgColor,
  getCardAccentBorder,
  getTimeUrgencyColor,
  getPaymentStatusLabel,
  calculateTotal,
  isScheduledOrder,
} from '../utils'
import type { DeepOrder, OrderType } from '../types'

interface OrderCardProps {
  order: DeepOrder
  isSelected?: boolean
  onClick?: () => void
}

const TYPE_CHIP: Record<OrderType, { icon: typeof Bike; className: string }> = {
  delivery: { icon: Bike, className: 'bg-status-info/10 text-status-info' },
  pickup: { icon: ShoppingBag, className: 'bg-status-success/10 text-status-success' },
  dineIn: { icon: UtensilsCrossed, className: 'bg-status-neutral/10 text-status-neutral' },
}

export const OrderCard = memo(function OrderCard({
  order,
  isSelected = false,
  onClick,
}: OrderCardProps) {
  const { t } = useTranslation('orders')
  const { t: posT } = useTranslation('posOrdering')
  const orderType = getOrderType(order)
  const { bg: cardBg, border: cardBorder } = getConfirmationBgColor(order)
  const accentBorder = getCardAccentBorder(order)
  const urgencyColor = getTimeUrgencyColor(order.date)
  const total = calculateTotal(order)
  const itemCount = order.orderItems.length
  const customerName = order.deliveryAddress?.name.trim()
  const typeChip = TYPE_CHIP[orderType]
  const TypeIcon = typeChip.icon

  // Time display: scheduled time for pre-orders (commitedTime in future),
  // "vor 5 Min" for ASAP. Hover-tooltip shows the absolute placed time.
  const isPreOrder = isScheduledOrder(order)
  const TimeIcon = isPreOrder ? CalendarClock : Clock
  const timeLabel = isPreOrder
    ? formatTime(order.commitedTime!)
    : formatRelativeTime(order.date)
  const timeTooltip = isPreOrder
    ? `${t('order.scheduledFor')} ${formatTime(order.commitedTime!)} • ${t('order.orderedAt')} ${formatTime(order.date)}`
    : `${t('order.orderedAt')} ${formatTime(order.date)}`
  const timeColor = isPreOrder ? 'text-status-info' : urgencyColor

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.()
        }
      }}
      className={cn(
        'rounded-xl border border-s-4 cursor-pointer transition-all duration-150',
        'hover:shadow-md hover:shadow-black/[0.06] hover:-translate-y-px active:scale-[0.99] active:translate-y-0',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        cardBg,
        cardBorder,
        accentBorder,
        isSelected && 'ring-2 ring-primary shadow-md shadow-black/[0.06]',
        order.cancelStatus === 'cancelled' && 'opacity-60 saturate-50',
      )}
      onClick={onClick}
    >
      <div className="p-4">
        {/* Row 1: Order ID + time (with urgency color) + total */}
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-display font-bold text-xl text-foreground tabular-nums">#{order.id}</span>
            <div
              className={cn('flex items-center gap-1 text-sm font-semibold', timeColor)}
              title={timeTooltip}
            >
              <TimeIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="whitespace-nowrap">{timeLabel}</span>
            </div>
          </div>
          <span className="font-display font-bold text-xl text-foreground tabular-nums shrink-0">{formatCurrency(total)}</span>
        </div>

        {/* Row 2: Items + payment */}
        <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
          <span className="truncate">
            <span className="font-medium text-foreground/80">
              {t(itemCount === 1 ? 'order.item' : 'order.item_plural', { count: itemCount })}
            </span>
            {order.orderItems.slice(0, 2).map((item, idx) => (
              <span key={item.id}>
                {idx === 0 ? ': ' : ', '}
                {item.name}
              </span>
            ))}
            {itemCount > 2 && <span> +{itemCount - 2}</span>}
          </span>
          <span className="shrink-0 rounded-md border border-border/70 bg-muted/60 px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
            {order.posPaymentUnavailable ? posT('payment.checkStatus') : order.posPayment ? order.posPayment.pendingRefundCents ? posT('refund.pending') : order.posPayment.refundedCents === order.posPayment.amountCents ? posT('refund.status.confirmed') : order.posPayment.refundedCents ? posT('refund.partial') : posT('payment.paid')
              : order.posRestaurantId ? posT('submitted.unpaid') : getPaymentStatusLabel(order.paymentStatus)}
          </span>
        </div>

        {/* Row 2b: Customer name (when provided) */}
        {customerName && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1 min-w-0">
            <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
            <span className="truncate">{customerName}</span>
          </div>
        )}

        {/* Row 3: Status badge + order type + phone */}
        <div className="flex items-center justify-between gap-2 mt-2.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <OrderStatusBadge status={order.status} cancelStatus={order.cancelStatus} />
            <span className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold shrink-0', typeChip.className)}>
              <TypeIcon className="h-3 w-3" />
              {t(`orderType.${orderType}`)}
            </span>
          </div>
          {order.telephone && (
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground tabular-nums shrink-0" dir="ltr">
              <Phone className="h-3 w-3" />
              {order.telephone}
            </span>
          )}
        </div>
      </div>
    </div>
  )
})
